import { applyVideoFetchPrefix } from '../../utils/videoUrlPrefix';

/**
 * Generates an IntersectionObserver callback for lazy-loading images.
 * Loads images when their anchor wrappers become visible in the viewport.
 *
 * @param {boolean} [trackFalsePositive=false] - Whether to track false positives for zoom gallery. It's optional
 * @returns {Function} IntersectionObserver callback.
 */
export const generateImageObserverCallback = (trackFalsePositive = false) => {
  return (entries, observer) => {
    entries.forEach((entry) => {
      // Track a false positve on zoom gallery
      if (trackFalsePositive) {
        if (entry.isIntersecting && entry.intersectionRatio == 1 && entry.target.style.order !== '1') {
          return;
        }
      }

      if (entry.isIntersecting) {
        // Intersect image element
        const anchor = entry.target;
        // Disconnect observer if an anchor already contains an image
        if (!Object.keys(anchor.dataset).length) {
          return observer.disconnect();
        }

        // Build a picture element
        const pictureElement = document.createElement('picture');
        const imageElement = document.createElement('img');
        const mediaConditions = anchor.dataset.sizes?.split(',');
        const srcsetArray = anchor.dataset.srcset?.split('|');
        delete anchor.dataset['sizes'];
        delete anchor.dataset['srcset'];

        mediaConditions?.forEach((mediaCondition, index) => {
          const sourceElement = document.createElement('source');
          sourceElement.media = mediaCondition;
          sourceElement.srcset = srcsetArray[index];
          // Append every source element in picture element
          pictureElement.appendChild(sourceElement);
        });

        // Append image element in picture element
        pictureElement.appendChild(imageElement);

        // This scans all data-* attributes and set each image attribute. Then, removes each data-* attribute
        for (let key in anchor.dataset) {
          imageElement[key] = anchor.dataset[key];
          delete anchor.dataset[key];
        }

        // Appends image element into anchor and set an onload function
        imageElement.onload = () => observer.disconnect();
        anchor.appendChild(pictureElement);

        // Preload potential next and previous images in the standard and zoom galleries
        preloadImages(anchor);
      }
    });
  };
};

/**
 * Preloads images for the zoom gallery.
 * @param {HTMLDivElemente | HTMLAnchorElement} anchor - the anchor container element
 */
const preloadImages = (anchor) => {
  const isZoom = anchor.id.includes('zoom_');
  const nodes = Array.from(anchor.parentNode.querySelectorAll('div[id*="carousel_"], a[id*="carousel_"]'));
  const currentIndex = nodes.findIndex((node) => node.id === anchor.id);
  let nextEl = null;
  let previousEl = null;

  // Check next and previous elements
  if (currentIndex + 1 < nodes.length) {
    nextEl = nodes[currentIndex + 1];
  }
  if (isZoom && currentIndex - 1 >= 0) {
    previousEl = nodes[currentIndex - 1];
  }

  // Build and link elements on the head for preloading them whenever they exist
  nextEl && Object.keys(nextEl.dataset).length && buildLink(nextEl);
  previousEl && Object.keys(previousEl.dataset).length && buildLink(previousEl);
};

/**
 * Extracts data information and builds a preload link element for a given image container.
 * @param {HTMLDivElement} el - The anchor container element.
 */
const buildLink = (el) => {
  const link = document.createElement('link');
  const sizes = window.innerWidth < 990 ? el.dataset.sizes.split(',')[0] : el.dataset.sizes.split(',')[1];
  link.rel = 'preload';
  link.as = 'image';
  link.imageSizes = sizes;
  link.imageSrcset = window.innerWidth < 990 ? el.dataset.srcset.split('|')[0] : el.dataset.srcset.split('|')[1];
  document.getElementsByTagName('head')[0].appendChild(link);
};

/**
 * Generates options for the IntersectionObserver used for images.
 *
 * @param {HTMLElement|null} rootElement - The root element for the observer.
 * @returns {Object} IntersectionObserver options.
 */
export const generateImageObserverOptions = (rootElement) => {
  return {
    root: rootElement || null,
    rootMargin: '0px',
    threshold: 0.1, // Don't use 1.0 because it is unrealistic to meet on Safari browsers
  };
};

/**
 * Generates an IntersectionObserver callback for lazy-loading videos.
 * Loads videos when their wrappers become visible in the viewport.
 *
 * @param {Array} autoplayedVideos - Array to store autoplayed video elements. It's optional
 * @param {boolean} [trackFalsePositive=false] - Whether to track false positives for zoom gallery. It's optional
 * @returns {Function} IntersectionObserver callback.
 */
export const generateVideoObserverCallback = (autoplayedVideos, trackFalsePositive = false) => {
  return (entries) => {
    entries.forEach((entry) => {
      const videoElement = entry.target.querySelector('video');
      const videoPlayer = entry.target.querySelector('button');
      const hiddenElement = entry.target.querySelector('span.visually-hidden');
      const videoSources = entry.target.querySelectorAll('source');

      if (!videoElement || !videoSources.length) {
        return console.error('There was no video element found');
      }

      // Track a false positve on zoom gallery
      if (trackFalsePositive) {
        if (entry.isIntersecting && entry.intersectionRatio == 1 && entry.target.style.order !== '1') {
          return;
        }
      }
      if (entry.isIntersecting) {
        // Intersect video element
        !videoElement.hasAttribute('data-loaded') &&
          videoSources.forEach((videoSource) => {
            const targetSrcRaw = videoSource.dataset.src;
            if (!targetSrcRaw) return;
            const targetSrc = applyVideoFetchPrefix(targetSrcRaw, 'pdp');

            if (videoSource.src !== targetSrc) {
              videoSource.src = targetSrc;
              delete videoSource.dataset['src'];
            }
          });
        autoplayedVideos?.push({ videoElement, videoPlayer, hiddenElement });
        !videoElement.hasAttribute('data-loaded') && videoElement.load();
        videoElement.setAttribute('data-loaded', true);
        videoElement.play();
      } else {
        videoElement.pause();
      }
    });
  };
};

/**
 * Generates options for the IntersectionObserver used for videos.
 *
 * @param {HTMLElement|null} rootElement - The root element for the observer.
 * @returns {Object} IntersectionObserver options.
 */
export const generateVideoObserverOptions = (rootElement) => {
  return {
    root: rootElement || null,
    rootMargin: '0px',
    threshold: 0.5, // Don't use 1.0 because it is unrealistic to meet on Safari browsers
  };
};
